import { useNavigate } from "react-router-dom";
import { processoInfo, etapaLabel } from "../../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa, statusInfo } from "../../../utils/solicitacoes/statusConfig";

// Mensagem (título + descrição) por código de ação — reflete o que realmente aconteceu em vez de
// um "Ação realizada com sucesso" genérico repetido para tudo.
const MENSAGENS_ACAO = {
    APROVAR: { titulo: "Aprovação realizada com sucesso", descricao: "A solicitação foi encaminhada para a próxima etapa." },
    REPROVAR: { titulo: "Reprovação registrada", descricao: "A solicitação foi encerrada por reprovação." },
    RETIFICAR: { titulo: "Enviada para retificação", descricao: "A solicitação retornou para a etapa anterior para ajustes." },
    RETORNAR: { titulo: "Solicitação retornada", descricao: "A solicitação retornou para revisão." },
    CONCLUIR: { titulo: "Finalizada com sucesso", descricao: "O processo foi concluído." },
    CANCELAR: { titulo: "Cancelada", descricao: "A solicitação foi encerrada conforme solicitado." },
    ENVIAR: { titulo: "Enviada com sucesso", descricao: "A solicitação foi encaminhada para a próxima etapa." },
    CONFIRMAR: { titulo: "Confirmação registrada", descricao: "A solicitação foi encaminhada para a próxima etapa." },
    REGISTRAR: { titulo: "Registro realizado com sucesso", descricao: "A solicitação foi encaminhada para a próxima etapa." },
    LANCADO: { titulo: "Lançamento registrado", descricao: "A solicitação avançou para a próxima etapa." },
    CRIAR: { titulo: "Solicitação criada com sucesso", descricao: "A solicitação foi registrada e encaminhada para a primeira etapa do fluxo." },
    FINALIZAR_ESTOQUE: { titulo: "Finalizada — produto já em estoque", descricao: "A solicitação foi encerrada sem necessidade de compra." },
};

const STATUS_ENCERRADO = new Set(["FINALIZADA", "CANCELADA", "ELIMINADA"]);

// Selo com ícone de check — badge circular pequeno ao lado do título, puramente visual.
function CheckBadge() {
    return (
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5">
                <path d="M5 13 L10 18 L19 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}

// Tela de confirmação genérica exibida após QUALQUER movimentação de tarefa bem-sucedida no módulo
// de Solicitações (aprovar, reprovar, retificar, encaminhar, confirmar, concluir, cancelar etc.) —
// substitui o redirecionamento automático e o "toast.success" isolado. Único ponto de verdade para
// a mensagem por ação: quem chama só informa o código da ação e a solicitação já atualizada (dados
// vindos do backend, nunca inventados aqui).
export default function AcaoConcluidaScreen({ acao, etapaAnterior, solicitacao, onVerSolicitacao }) {
    const navigate = useNavigate();

    const processo = processoInfo(solicitacao.tipoProcesso);
    const tipoProcessoLabel = processo?.label || solicitacao.tipoProcesso;
    const encerrada = STATUS_ENCERRADO.has(solicitacao.status);

    const etapaAnteriorLabel = etapaAnterior
        ? (etapaLabel(solicitacao.tipoProcesso, etapaAnterior) || humanizarEtapa(etapaAnterior))
        : null;
    const proximaEtapaLabel = !encerrada && solicitacao.etapaAtual
        ? (etapaLabel(solicitacao.tipoProcesso, solicitacao.etapaAtual) || humanizarEtapa(solicitacao.etapaAtual))
        : null;

    const destinatarioTxt = !encerrada
        ? (solicitacao.responsavelAtualNome
            ? `Encaminhada para ${solicitacao.responsavelAtualNome}.`
            : solicitacao.grupoResponsavelAtualNome
                ? `Encaminhada para o grupo ${solicitacao.grupoResponsavelAtualNome}.`
                : null)
        : null;

    const { titulo, descricao } = MENSAGENS_ACAO[acao] || { titulo: "Ação realizada com sucesso", descricao: "A tarefa foi movimentada corretamente." };

    return (
        <main className="flex justify-center items-start pt-8 min-h-[70vh] px-4">
            <div className="w-full max-w-xl bg-white p-3 px-4 rounded-lg shadow-lg">
   
                <div className="mt-4 flex items-start gap-3">
                    <CheckBadge />
                    <div>
                        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground leading-snug">
                            {titulo}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Solicitação{" "}
                            <button onClick={onVerSolicitacao} className="text-primary underline underline-offset-4 hover:opacity-80">
                                #{solicitacao.numero}
                            </button>
                            {" "}· {tipoProcessoLabel}
                        </p>
                    </div>
                </div>

                <p className="mt-4 text-sm text-muted-foreground">
                    {descricao} Você pode acessar os detalhes, os anexos e os comentários desta solicitação.
                </p>

                <div className="mt-4 rounded-lg border border-border p-4 flex flex-col gap-2 text-sm text-muted-foreground">
                    {etapaAnteriorLabel && (
                        <div className="flex justify-between gap-4">
                            <span>Etapa anterior</span>
                            <span className="text-foreground font-medium text-right">{etapaAnteriorLabel}</span>
                        </div>
                    )}
                    {encerrada ? (
                        <div className="flex justify-between gap-4">
                            <span>Status</span>
                            <span className="text-foreground font-medium text-right">{statusInfo(solicitacao.status).label}</span>
                        </div>
                    ) : proximaEtapaLabel && (
                        <div className="flex justify-between gap-4">
                            <span>Próxima etapa</span>
                            <span className="text-foreground font-medium text-right">{proximaEtapaLabel}</span>
                        </div>
                    )}
                    {destinatarioTxt && (
                        <div className="flex justify-between gap-4">
                            <span>Responsável</span>
                            <span className="text-foreground font-medium text-right">{destinatarioTxt.replace(/^Encaminhada para (o grupo )?/, "").replace(/\.$/, "")}</span>
                        </div>
                    )}
                </div>

                <button
                    onClick={onVerSolicitacao}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80"
                >
                    Acessar solicitação #{solicitacao.numero}
                </button>

                <hr className="my-6 border-border" />

                <h2 className="text-sm font-semibold text-foreground/80 mb-3">O que você deseja fazer?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        onClick={() => navigate("/solicitacoes/central-tarefas")}
                        className="rounded-lg border border-border p-3 text-sm text-left text-primary hover:bg-primary/5 transition-colors"
                    >
                        Ir para a central de tarefas
                    </button>
                    <button
                        onClick={() => navigate("/solicitacoes/todas")}
                        className="rounded-lg border border-border p-3 text-sm text-left text-primary hover:bg-primary/5 transition-colors"
                    >
                        Ir para todas as solicitações
                    </button>
                </div>
            </div>
        </main>
    );
}
